import React, { useState } from 'react';
import { Lightbulb } from 'lucide-react';
import { SegmentedControl, cn } from '../../../ui';
import DashCard, { CardEmpty } from '../shared/DashCard';
import ScoreRing from '../shared/ScoreRing';
import TrendChart from '../shared/TrendChart';
import { subjectTone, trendPoints } from '../studentHomeRules';

const RANGES = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
];

const SUBJECT_TONE_CLASSES = {
  emerald: 'tw-border-success/20 tw-bg-success-soft',
  sky: 'tw-border-info/20 tw-bg-info-soft',
  amber: 'tw-border-warning/20 tw-bg-warning-soft',
  primary: 'tw-border-primary/20 tw-bg-primary-soft',
};

const SUBJECT_LABEL_CLASSES = {
  emerald: 'tw-text-success-fg',
  sky: 'tw-text-info-fg',
  amber: 'tw-text-warning-fg',
  primary: 'tw-text-primary-soft-fg',
};

const SubjectTile = ({ subject }) => {
  const tone = subjectTone(subject.label);
  return (
    <li className={cn('tw-flex tw-min-w-0 tw-flex-col tw-rounded tw-border tw-border-solid tw-px-2.5 tw-py-3 tw-text-center', SUBJECT_TONE_CLASSES[tone])}>
      <span className="tw-line-clamp-2 tw-text-[0.6875rem] tw-font-semibold tw-leading-snug tw-text-content-muted" title={subject.name}>
        {subject.name}
      </span>
      <span className="tw-mt-auto tw-pt-1 tw-text-xl tw-font-medium tw-leading-tight tw-text-content">{subject.score}%</span>
      <span className={cn('tw-text-[0.6875rem] tw-font-semibold', SUBJECT_LABEL_CLASSES[tone])}>{subject.label}</span>
    </li>
  );
};

const PerformanceCard = ({ performance, className }) => {
  const [range, setRange] = useState('week');
  const active = performance?.[range];
  const subjects = active?.subjects || [];

  return (
    <DashCard
      title="Performance Overview"
      className={className}
      action={<SegmentedControl label="Performance period" size="xs" value={range} onChange={setRange} options={RANGES} />}
    >
      {active?.hasData ? (
        <>
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 card-md:tw-flex-row card-md:tw-items-center card-md:tw-gap-6">
            <ScoreRing score={active.overall} label={`Overall score ${active.overall}%`} className="tw-h-[132px] tw-w-[132px]">
              <span className="tw-text-2xl tw-font-extrabold tw-leading-tight tw-text-content">{active.overall}%</span>
              <span className="tw-text-[0.6875rem] tw-text-content-subtle">Overall Score</span>
            </ScoreRing>
            <div className="tw-w-full tw-min-w-0 tw-flex-1">
              <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-medium tw-text-content-muted">Performance Trend</h3>
              <TrendChart points={trendPoints(active.trend)} />
            </div>
          </div>
          {subjects.length > 0 && (
            <ul className="tw-m-0 tw-mt-4 tw-grid tw-list-none tw-grid-cols-2 tw-gap-2.5 tw-p-0 card-md:tw-grid-cols-4">
              {subjects.map((subject) => <SubjectTile key={subject.name} subject={subject} />)}
            </ul>
          )}
          {active.percentileLabel && (
            <p className="tw-m-0 tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-primary-soft tw-px-3 tw-py-2.5 tw-text-xs tw-font-medium tw-text-primary-soft-fg">
              <Lightbulb size={14} aria-hidden="true" className="tw-shrink-0" />
              {active.percentileLabel}
            </p>
          )}
        </>
      ) : (
        <CardEmpty>No scored activity in this period yet. Complete an AI quiz, an assessment or coding practice to see your trend.</CardEmpty>
      )}
    </DashCard>
  );
};

export default PerformanceCard;
